import { useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";
import { CategoryFilter } from "~/components/common/CategoryFilter";
import { CountryFilter } from "~/components/common/CountryFilter";
import { SearchBar } from "~/components/common/SearchBar";
import { Pagination } from "~/components/common/Pagination";
import { MerchantRow } from "~/components/merchant/MerchantRow";
import {
  getMerchantVerification,
  primaryCountryCode,
} from "~/lib/data/merchant";
import type {
  MerchantCategory,
  MerchantCategoryRelationship,
  MerchantClassification,
  MerchantCountry,
} from "~/lib/types";

type MerchantRecord = Record<string, unknown>;

type Props = {
  merchants: MerchantRecord[];
  merchantCountries: MerchantCountry[];
  categories: MerchantCategory[];
  categoryRelationships: MerchantCategoryRelationship[];
  classifications: MerchantClassification[];
};

const labelClass =
  "block font-heading text-xs font-bold uppercase tracking-[0.12em] text-text-light";

const PAGE_SIZE = 9;

export const MerchantDirectory = ({
  merchants,
  merchantCountries,
  categories,
  categoryRelationships,
  classifications,
}: Props) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const query = searchParams.get("q")?.toLowerCase() ?? "";
  const country = searchParams.get("country") ?? "";
  const categoryId = searchParams.get("category") ?? "";

  const merchantIdsForCountry = country
    ? new Set(
        merchantCountries
          .filter(mc => mc.merchant_country_code === country)
          .map(mc => mc.merchant_id),
      )
    : null;

  const merchantIdsByCategory = useMemo(() => {
    const map = new Map<string, Set<string>>();
    for (const c of classifications) {
      const existing = map.get(c.item_id) ?? new Set<string>();
      existing.add(c.merchant_id);
      map.set(c.item_id, existing);
    }
    return map;
  }, [classifications]);

  const merchantIdsForCategory = categoryId
    ? (merchantIdsByCategory.get(categoryId) ?? new Set<string>())
    : null;

  const filtered = merchants.filter((m: MerchantRecord) => {
    const id = String(m.id);
    if (merchantIdsForCountry && !merchantIdsForCountry.has(id)) return false;
    if (merchantIdsForCategory && !merchantIdsForCategory.has(id)) return false;
    if (query) {
      const name = String(m.merchant_name ?? "").toLowerCase();
      const commonName = String(m.merchant_common_name ?? "").toLowerCase();
      const description = String(m.merchant_description ?? "").toLowerCase();
      return (
        name.includes(query) ||
        commonName.includes(query) ||
        description.includes(query)
      );
    }
    return true;
  });

  const hasFilters = !!(query || country || categoryId);

  // Pagination (URL-driven, clamped so filter changes never land on an empty page).
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const requestedPage = Number(searchParams.get("page")) || 1;
  const currentPage = Math.min(Math.max(1, requestedPage), totalPages);
  const pageItems = filtered.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE,
  );

  const goToPage = (page: number) => {
    const params = new URLSearchParams(searchParams.toString());
    if (page <= 1) {
      params.delete("page");
    } else {
      params.set("page", String(page));
    }
    const qs = params.toString();
    navigate(`/merchants${qs ? `?${qs}` : ""}`);
    if (typeof window !== "undefined") window.scrollTo({ top: 0 });
  };

  return (
    <div className="mt-8 grid gap-8 lg:grid-cols-[260px_1fr]">
      {/* Filters */}
      <aside className="space-y-5">
        <div>
          <span className={labelClass}>{t("MerchantsPage.filter-search")}</span>
          <SearchBar className="mt-2 w-full" />
        </div>
        {merchantCountries.length > 0 && (
          <div>
            <span className={labelClass}>
              {t("MerchantsPage.filter-country")}
            </span>
            <CountryFilter
              merchantCountries={merchantCountries}
              className="mt-2 w-full"
            />
          </div>
        )}
        {categories.length > 0 && (
          <div>
            <span className={labelClass}>
              {t("MerchantsPage.filter-category")}
            </span>
            <CategoryFilter
              categories={categories}
              relationships={categoryRelationships}
              className="mt-2 w-full"
            />
          </div>
        )}
        {hasFilters && (
          <button
            type="button"
            onClick={() => navigate("/merchants")}
            className="w-full rounded-lg border border-border-shell px-4 py-2.5 font-heading text-sm font-semibold uppercase tracking-wider text-text-medium transition hover:bg-surface-elevated hover:text-text-full"
          >
            {t("MerchantsPage.clear-filters")}
          </button>
        )}
      </aside>

      {/* Results */}
      <div>
        <p className="text-sm text-text-medium">
          {t("MerchantsPage.found", { count: filtered.length })}
        </p>
        <div className="mt-4 space-y-3">
          {pageItems.map((m: MerchantRecord) => (
            <MerchantRow
              key={String(m.id)}
              id={String(m.id)}
              name={m.merchant_name as string | null}
              commonName={m.merchant_common_name as string | null}
              description={m.merchant_description as string | null}
              logoUrl={m.merchant_image_url_logo as string | null}
              verification={getMerchantVerification(m)}
              country={primaryCountryCode(String(m.id), merchantCountries)}
            />
          ))}
        </div>
        {filtered.length === 0 && (
          <p className="mt-10 text-center text-text-light">
            {t("MerchantsPage.no-merchants")}
          </p>
        )}
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={goToPage}
        />
      </div>
    </div>
  );
};
